<template>
    <TheStdLayout :title="$tc('Pupil', 2)">

        <div class="px-4 py-6 mx-auto sm:px-6 md:px-8">

			<Dropdown class="top-16" wrapperClasses="sticky top-2">
				<template #trigger>
					<FloatingPlusButton class="ml-4" :pulsate="pupilsStore.loadedOnce && pupilsStore.items.length === 0 && filterEmpty" />
				</template>
				<template #content>
					<DropdownMenu class="w-48">
						<DropdownMenuItem>
							<a @click="handlePupilCreateRequest">
								<UserPlusIcon class="w-5 h-5 mr-2" />
								{{ $t('Add pupil') }}
							</a>
						</DropdownMenuItem>
						<DropdownMenuItem>
							<a @click="handleImportRequest">
								<UsersIcon class="w-5 h-5 mr-2" />
								{{ $t('Import') }}
							</a>
						</DropdownMenuItem>
					</DropdownMenu>
				</template>
			</Dropdown>


			<div class="lg:-mx-8 2xl:mx-0 max-w-7xl">

				<div v-if="showFilter" class="mb-10">
					<PupilFilter
						@change="handleFilterChange"
						@classroom-change="handleBulkClassroomChange"
						:classrooms="classroomsStore.items"
					/>

					<div v-if="showBulkActionsButton"
						class="flex items-center gap-2 p-2 rounded-lg transition-opacity duration-300 w-fit"
						:class="areBulkActionsExpanded ? 'bg-indigo-50 text-indigo-500' : ''"
					>
						<button type="button"
							@click="areBulkActionsExpanded = !areBulkActionsExpanded"
							class="btn btn-sm btn-ghost hover:bg-base-100 normal-case"
							:class="areBulkActionsExpanded ? 'text-indigo-500 hover:bg-indigo-100' : ''"
							:disabled="bulkSelectedPupils.length === 0 && !areBulkActionsExpanded"
						>
							<div v-if="!areBulkActionsExpanded">
								<svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 256 256" class="w-4 h-4 mr-1">
									<path d="M224,128a8,8,0,0,1-8,8H128a8,8,0,0,1,0-16h88A8,8,0,0,1,224,128ZM128,72h88a8,8,0,0,0,0-16H128a8,8,0,0,0,0,16Zm88,112H128a8,8,0,0,0,0,16h88a8,8,0,0,0,0-16ZM82.34,42.34,56,68.69,45.66,58.34A8,8,0,0,0,34.34,69.66l16,16a8,8,0,0,0,11.32,0l32-32A8,8,0,0,0,82.34,42.34Zm0,64L56,132.69,45.66,122.34a8,8,0,0,0-11.32,11.32l16,16a8,8,0,0,0,11.32,0l32-32a8,8,0,0,0-11.32-11.32Zm0,64L56,196.69,45.66,186.34a8,8,0,0,0-11.32,11.32l16,16a8,8,0,0,0,11.32,0l32-32a8,8,0,0,0-11.32-11.32Z"></path>
								</svg>
							</div>
							<div v-else>
								<XMarkIcon class="w-4 h-4 mr-1 text-indigo-500" />
							</div>
							{{ $t('Classroom bulk actions') }}
						</button>

						<template v-if="areBulkActionsExpanded">
							<button type="button"
								@click="executeBulkAdd"
								class="btn btn-sm btn-primary normal-case"
								:disabled="bulkSelectedPupils.length === 0"
							>
								<PlusIcon class="w-4 h-4 mr-1" />
								{{ $t('Add to classroom') }}
							</button>

							<button type="button"
								@click="executeBulkRemove"
								class="btn btn-sm btn-primary normal-case"
								:disabled="bulkSelectedPupils.length === 0"
							>
								<NoSymbolIcon class="w-4 h-4 mr-1" />
								{{ $t('Remove from classroom') }}
							</button>
						</template>
					</div>
				</div>

				<EmptyState
					v-if="!pupilsStore.loading && pupilsStore.loadedOnce && pupilsStore.items.length === 0 && filterEmpty"
					:title="$t('No pupils yet')"
					:description="$t('Start with adding your first pupil')"
				/>

				<div v-else-if="pupilsStore.loading">
					<div class="flex justify-center items-center py-20">
						<span class="loading loading-spinner loading-lg"></span>
					</div>
				</div>

                <div v-else class="w-full">

                    <div
						class="grid w-full items-end font-medium border-b border-base-200 pb-3 text-sm text-base-content/70"
						:class="[showSymbols? 'grid-cols-[1fr_2fr_2.5fr_3fr] md:grid-cols-[1fr_2fr_2fr_2.5fr_3fr]' : 'grid-cols-[.5fr_2fr_2.5fr_3fr] md:grid-cols-[.5fr_2fr_2fr_2.5fr_3fr]']"
					>
                        <div class="px-2 lg:pl-4">
                            <input
                                v-if="showBulkActions"
                                type="checkbox"
                                class="checkbox checkbox-sm checkbox-accent print:hidden mb-2"
                                :checked="allPupilsSelected"
                                @change="toggleAllPupilsSelection"
                            />
                            <div v-if="showSymbols" class="sr-only">{{ $t('Symbol') }}</div>
                        </div>

                        <div class="table-col-title flex flex-col gap-2">
							<div>
								<SortableHeader property="firstName" :order="pupilsStore.order" @change="pupilsStore.applyOrder">
									{{ $t('First name') }}
								</SortableHeader>
							</div>
							<div>
								<SortableHeader property="lastName" :order="pupilsStore.order" @change="pupilsStore.applyOrder">
									{{ $t('Last name') }}
								</SortableHeader>
							</div>
                        </div>

                        <div class="table-col-title hidden md:block">
                            <SortableHeader property="birthdate" :order="pupilsStore.order" @change="pupilsStore.applyOrder">
                                {{ $t('Date of birth') }}
                            </SortableHeader>
                        </div>

                        <div class="table-col-title">
							<div v-if="showCloudFileColumn">
								{{ $t('Pupil file') }}
							</div>
                        </div>

                        <div class="text-right px-6">
                            <span class="sr-only">{{ $t('Actions') }}</span>
                        </div>
                    </div>

                    <div
						class="grid py-3 gap-y-4"
						:class="[showSymbols? 'grid-cols-[1fr_2fr_2.5fr_3fr] md:grid-cols-[1fr_2fr_2fr_2.5fr_3fr]' : 'grid-cols-[.5fr_2fr_2.5fr_3fr] md:grid-cols-[.5fr_2fr_2fr_2.5fr_3fr]']"
					>

                        <!-- <div v-show="!pupilsStore.loadedOnce" class="py-4 col-span-full">
                            <Skeleton content-type="userlist" :items="3" class="m-4" thumbnail-classes="mask mask-squircle" />
                        </div> -->

                        <div v-show="!filterEmpty && pupilsStore.items.length === 0" class="px-4 py-6 italic text-center col-span-full">
                            {{ $tc('Results', 0) }}
                        </div>

                        <template v-for="(pupil, key) in pupilsStore.items" :key="key">
                            <div class="px-2 lg:pl-4 print:py-1">
                                <input
                                    v-if="showBulkActions"
                                    type="checkbox"
                                    class="checkbox checkbox-sm checkbox-accent print:hidden"
                                    :checked="isPupilSelected(pupil)"
                                    @change="togglePupilSelection(pupil)"
                                />
                                <PupilSymbol v-else-if="showSymbols" :pupil="pupil" class="w-14" />
                            </div>

                            <div class="px-2 lg:pl-4 overflow-x-auto print:py-1 flex flex-col">
								<div class="truncate whitespace-nowrap">
									{{ pupil.firstName }}
								</div>
								<div class="truncate whitespace-nowrap">
									{{ pupil.lastName }}
								</div>
                            </div>

                            <div class="hidden md:block text-start px-4 whitespace-nowrap print:py-1">
                                {{ formatBirthdate(pupil.birthdate) }}
                            </div>

                            <div class="px-4 print:py-1">
								<div v-if="showCloudFileColumn">
									<AttachmentsUploadedFile
										v-if="pupil.files?.length"
										:file="pupil.files[0]"
										:readOnly="true"
										class="max-w-48 text-sm"
									/>
								</div>
                            </div>

                            <div class="px-0 whitespace-nowrap text-right text-sm font-medium flex justify-end items-center gap-2 sm:gap-4 print:py-1">
                                <div class="print:hidden join gap-0.5 [&>*:first-child]:rounded-l-full [&>*:last-child]:rounded-r-full">
                                    <button :title="$tc('Observation', 2)"
                                        class="btn btn-ghost bg-base-100 join-item"
                                        @click="selectedPupilForObservation = pupil"
                                    >
                                        <EyeIcon class="w-5 h-5"/>
                                    </button>
                                    <button v-if="hasCurriculum" @click="selectedPupilForEvaluation = pupil" :title="$tc('Evaluation', 2)"
                                        class="btn btn-ghost bg-base-100 join-item">
                                        <MapPinIcon class="w-5 h-5"/>
                                    </button>
                                    <button v-if="showStatistics" :title="$t('Insights')" class="btn btn-ghost bg-base-100 join-item"
                                        @click="insightsSelectedPupil = pupil">
                                        <ChartBarIcon class="w-5 h-5"></ChartBarIcon>
                                    </button>
                                </div>
                                <button :title="$t('Edit pupil')" class="btn btn-ghost btn-circle bg-base-100"
                                    @click="handlePupilEditRequest(pupil)">
                                    <PencilIcon class="w-5 h-5"></PencilIcon>
                                </button>
                            </div>

							<hr class="col-span-full last:hidden">
                        </template>
                    </div>

					<Pagination v-if="pupilsStore.page" :page="pupilsStore.page" @requestPage="pupilsStore.goToPage" />

                </div>
            </div>
        </div>

        <PupilEditor v-if="selectedPupilForEditing" :pupil="selectedPupilForEditing" @requestClose="hideEditForm" :scope="scope" />

        <PupilImporter v-if="showImporter" @requestClose="hideImporter" :classroom="pupilImportClassroom" />

        <InsightsDrawer v-if="insightsSelectedPupil"
            :title="$t('Insights') + ' - ' + insightsSelectedPupil.firstName + ' ' + insightsSelectedPupil.lastName"
            sessionStateKey="insights.pupil"
            :pupil="insightsSelectedPupil"
            :showPupilSelector="false"
            :defaultDateRange="dateRangeCurrentWeek"
            :showDateRangeSelector="true"
            :insights="[
                { value: 'activity', label: $tc('Activity', 2) },
                { value: 'feedback', label: $t('Feedback') },
				//{ value: 'social', label: this.$t('Social interaction') },
            ]" @requestClose="() => { insightsSelectedPupil = null; }"
        ></InsightsDrawer>

        <ObservationsDrawer
            v-if="selectedPupilForObservation"
            :pupil="selectedPupilForObservation"
			:readOnly="scope !== 'classroom'"
			:classroom="scope === 'classroom' ? classroomsStore.currentClassroom : null"
            @requestUnmount="selectedPupilForObservation = null"
        />

        <PupilGoalEvaluation v-if="selectedPupilForEvaluation"
			:pupil="selectedPupilForEvaluation"
			:withSchoolYearSelector="true"
			:showEvaluatedGoalsOnly="scope !== 'classroom'"
			:readOnly="scope !== 'classroom'"
			:classroom="scope === 'classroom' ? classroomsStore.currentClassroom : null"
			@requestClose="selectedPupilForEvaluation = null"
		/>

		<PupilBulkAddModal
			v-if="showBulkAddModal && bulkClassroom"
			:selected-pupils="bulkSelectedPupils"
			:classrooms="classroomsStore.items"
			:current-classroom="bulkClassroom"
			@safeToUnmount="showBulkAddModal = false"
		/>

		<PupilBulkRemoveModal
			v-if="showBulkRemoveModal && bulkClassroom"
			:selected-pupils="bulkSelectedPupils"
			:current-classroom="bulkClassroom"
			@safeToUnmount="showBulkRemoveModal = false"
		/>

	</TheStdLayout>
</template>

<script lang="ts">

import Skeleton from '@/components/ui/Skeleton.vue';
import PupilSymbol from "@/components/ui/symbols/PupilSymbol.vue";

import { mapStores } from "pinia";
import { usePupilsStore } from "@/stores/Pupils.store";
import { useOrganisationsStore } from '@/stores/Organisations.store';
import PupilEditor from '@/components/PupilEditor.vue';
import PupilFilter from '@/components/PupilFilter.vue';

import {
	PlusIcon, PencilIcon, UserPlusIcon, UsersIcon, ChartBarIcon, MapPinIcon, EyeIcon,
	ChevronDownIcon, NoSymbolIcon, XMarkIcon
} from '@heroicons/vue/24/outline';
import { Classroom } from "@/models/Classroom.model";
import SortableHeader from "@/components/ui/SortableHeader.vue";
import { useDefaultOrderStore } from "@/stores/DefaultOrder.store";
import { DateTime, Interval } from "luxon";
import { useClassroomsStore } from "@/stores/Classrooms.store";
import PupilImporter from "@/components/PupilImporter.vue";
import { Pupil } from "@/models/Pupil.model";
import Dropdown from '@/components/ui/Dropdown.vue';
import DropdownMenu from '@/components/ui/DropdownMenu.vue';
import DropdownMenuItem from '@/components/ui/DropdownMenuItem.vue';

import AttachmentsUploadedFile from "@/components/AttachmentsUploadedFile.vue";
import InsightsDrawer from '@/components/InsightsDrawer.vue';
import PupilGoalEvaluation from './PupilGoalEvaluation.vue';
import ObservationsDrawer from "@/components/ObservationsDrawer.vue";
import PupilBulkAddModal from '@/components/PupilBulkAddModal.vue';
import PupilBulkRemoveModal from '@/components/PupilBulkRemoveModal.vue';
import { PupilFilterDef } from '@/stores/filters/PupilFilterDef';
import EmptyState from '@/components/ui/EmptyState.vue';
import Pagination from '@/components/ui/Pagination.vue';

export default {

	components: {
		PupilImporter,
		SortableHeader,
		Skeleton,
		PupilSymbol,
		PupilEditor,
		PupilFilter,
		ChartBarIcon,
		InsightsDrawer,
		PlusIcon, PencilIcon, UserPlusIcon, UsersIcon, ChevronDownIcon, NoSymbolIcon, XMarkIcon,
		Dropdown, DropdownMenu, DropdownMenuItem,
		PupilGoalEvaluation,
		PupilBulkAddModal,
		PupilBulkRemoveModal,
		MapPinIcon,
		EyeIcon,
		AttachmentsUploadedFile,
		ObservationsDrawer,
		EmptyState,
		Pagination,
	},

	data() {
		return {
			selectedPupilForEditing: null,
			selectedPupilForObservation: null,
			showImporter: false,
			insightsSelectedPupil: null as Pupil | null,
			selectedPupilForEvaluation: null as Pupil | null,
			bulkClassroom: null as Classroom | null,
			bulkSelectedPupilIds: [] as string[],
			showBulkAddModal: false,
			showBulkRemoveModal: false,
			searchTimeout: null as number | null,
			defaultFilter: new PupilFilterDef(),
			areBulkActionsExpanded: false,
		};
	},

	props: {
		scope: {
			type: String,
			default: 'classroom',
		},
	},

	watch: {
		scope() {
			this.resetBulkState();
			this.loadAllPupilsForScope();
			if (this.scope === 'organisation') {
				this.classroomsStore.loadOnce();
			}
		},
		'pupilsStore.items'(newPupils: Pupil[]) {
			if (this.bulkClassroom !== null) {
				this.bulkSelectedPupilIds = newPupils.map((p: Pupil) => p.id);
			}
		},
	},

	computed: {
		...mapStores(usePupilsStore, useDefaultOrderStore, useClassroomsStore, useOrganisationsStore),

		dateRangeCurrentWeek() {
			return Interval.fromDateTimes(DateTime.now().startOf('week'), DateTime.now().endOf('week'));
		},

		showSymbols() {
			return this.scope === 'classroom';
		},

		showFilter() {
			return this.scope === 'organisation';
		},

		showStatistics() {
			return this.scope === 'classroom';
		},

		// Goals can only be consulted or evaluated for the school's own curriculum.
		hasCurriculum(): boolean {
			return !!this.organisationsStore.currentOrganisation?.curriculum;
		},

		formatBirthdate() {
			return (birthdate: DateTime | null) => {
				if (!birthdate) {
					return '';
				}
				return birthdate.toFormat('dd/MM/yyyy');
			};
		},

		pupilImportClassroom() {
			return this.scope === 'classroom' ? this.classroomsStore.currentClassroom : null;
		},

		/**
		 * Returns true if at least one pupil in the list has a file attachment.
		 * Used to conditionally show the "Pupil file" column.
		 */
		anyPupilHasFile() {
			return this.pupilsStore.items.some((pupil: Pupil) => pupil.files?.length > 0);
		},

		showCloudFileColumn() {
			return this.organisationsStore.getCurrentOrganisation().pupil_cloud_link_enabled === true && this.anyPupilHasFile;
		},

		showBulkActionsButton() {
			return this.scope === 'organisation' && this.bulkClassroom !== null;
		},

		showBulkActions() {
			return this.showBulkActionsButton && this.areBulkActionsExpanded;
		},

		allPupilsSelected() {
			const pupils = this.pupilsStore.items as Pupil[];
			return pupils.length > 0 && pupils.every((p: Pupil) => this.bulkSelectedPupilIds.includes(p.id));
		},

		bulkSelectedPupils() {
			return (this.pupilsStore.items as Pupil[]).filter((p: Pupil) => this.bulkSelectedPupilIds.includes(p.id));
		},

		/**
		 * Dynamic colspan for skeleton/empty-state rows,
		 * accounting for the optional symbol and pupil file columns.
		 */
		tableColSpan() {
			let cols = 4; // firstName, lastName, birthdate, actions
			if (this.showSymbols) cols++;
			if (this.showCloudFileColumn) cols++;
			if (this.showBulkActionsButton) cols++;
			return cols;
		},

		filterEmpty() {
			// Can't use this.pupilsStore.getCurrentFilter().isEmpty() here because the filter can include a classroom parameter and isEmpty() does not
			// consider the context in which the pupils are being viewed. isEmpty() would return false when a classroom filter is applied, while you'd expect it
			// to return true when used in classroom context.
			const currentFilter = this.pupilsStore.getCurrentFilter();
			if(!currentFilter) {
				return true;
			}
			return currentFilter.equals(this.defaultFilter);
		}
	},

	async created() {
		this.pupilsStore.resetDefaultOrder();
		this.resetBulkState();
		await this.loadAllPupilsForScope();
		if (this.scope === 'organisation') {
			this.classroomsStore.loadOnce();
		}
	},

	methods: {
		resetBulkState() {
			this.bulkClassroom = null;
			this.bulkSelectedPupilIds = [];
			this.areBulkActionsExpanded = false;
			this.showBulkAddModal = false;
			this.showBulkRemoveModal = false;
		},

		handleFilterChange(filter: any) {
			// buffer the change events to avoid excessive filtering while the user is typing
			if (this.searchTimeout) {
				clearTimeout(this.searchTimeout);
			}
			this.searchTimeout = setTimeout(() => {
				this.pupilsStore.applyFilter(filter);
			}, 400);
		},

		handleBulkClassroomChange(classroom: Classroom | null) {
			this.bulkClassroom = classroom;
			if (classroom === null) {
				this.bulkSelectedPupilIds = [];
				this.areBulkActionsExpanded = false;
			} else {
				this.bulkSelectedPupilIds = (this.pupilsStore.items as Pupil[]).map((p: Pupil) => p.id);
			}
		},

		isPupilSelected(pupil: Pupil): boolean {
			return this.bulkSelectedPupilIds.includes(pupil.id);
		},

		togglePupilSelection(pupil: Pupil) {
			const idx = this.bulkSelectedPupilIds.indexOf(pupil.id);
			if (idx >= 0) {
				this.bulkSelectedPupilIds.splice(idx, 1);
			} else {
				this.bulkSelectedPupilIds.push(pupil.id);
			}
		},

		toggleAllPupilsSelection() {
			if (this.allPupilsSelected) {
				this.bulkSelectedPupilIds = [];
			} else {
				this.bulkSelectedPupilIds = (this.pupilsStore.items as Pupil[]).map((p: Pupil) => p.id);
			}
		},

		executeBulkAdd() {
			if (!this.bulkClassroom || this.bulkSelectedPupilIds.length === 0) return;
			this.showBulkAddModal = true;
		},

		executeBulkRemove() {
			if (!this.bulkClassroom || this.bulkSelectedPupilIds.length === 0) return;
			this.showBulkRemoveModal = true;
		},

		async handlePupilCreateRequest() {
			this.selectedPupilForEditing = await this.pupilsStore.new(
				this.scope === 'classroom' ? this.classroomsStore.currentClassroom : null
			);
		},

		handlePupilEditRequest(pupil: Pupil) {
			this.selectedPupilForEditing = pupil;
		},

		async loadAllPupilsForScope() {
			await this.pupilsStore.loadForClassroom(
				this.scope === 'classroom' ? this.classroomsStore.currentClassroom : null,
				true
			);
			this.defaultFilter = this.pupilsStore.getCurrentFilter();
		},

		closeEditPopup() {
			this.selectedPupilForEditing = null;
		},

		closeObservationPopup() {
			this.selectedPupilForObservation = null;
		},

		hideEditForm() {
			this.selectedPupilForEditing = null;
		},

		handleImportRequest() {
			this.showImporter = true;
		},

		hideImporter() {
			this.showImporter = false;
		},
	},
};

</script>
